import { useCallback, useEffect, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { useI18n } from "@/i18n";
import {
  homepageBeforeAfterProjects,
  projectPrimaryAfter,
  projectPrimaryBefore,
  type BeforeAfterProject,
  type ProjectImage,
} from "@/config/projects";
import { cn } from "@/lib/utils";
import { trackAnalyticsEvent } from "@/lib/analytics";
import { localizeProject } from "@/i18n/project-content";

/**
 * Homepage before/after strip.
 * Shows the first three homepage gallery projects (not the full portfolio set).
 */
export function BeforeAfter() {
  const { t, locale } = useI18n();
  const projects = homepageBeforeAfterProjects.map((project) => localizeProject(project, locale));

  return (
    <section id="before-after" className="section-y">
      <div className="container-page">
        <SectionHeader
          eyebrow={t.beforeAfter.eyebrow}
          title={t.beforeAfter.title}
          body={t.beforeAfter.body}
        />

        <div className="mt-12 grid gap-8 lg:grid-cols-3">
          {projects.map((project) => (
            <figure key={project.id} className="min-w-0">
              <BeforeAfterSlider project={project} />
              <figcaption className="mt-4">
                <h3 className="text-[0.95rem] font-semibold tracking-tight text-foreground">
                  {project.title}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {project.location} · {project.system}
                </p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

type SliderSource = {
  id: string;
  title: string;
  before: ProjectImage;
  after: ProjectImage;
  width: number;
  height: number;
};

function resolveSliderSource(project: BeforeAfterProject | SliderSource): SliderSource {
  if ("beforeImages" in project && Array.isArray(project.beforeImages)) {
    const full = project as BeforeAfterProject;
    return {
      id: full.id,
      title: full.title,
      before: projectPrimaryBefore(full),
      after: projectPrimaryAfter(full),
      width: full.width,
      height: full.height,
    };
  }
  return project as SliderSource;
}

/** Reusable drag / touch / keyboard before-after comparison slider. */
export function BeforeAfterSlider({
  project,
  className,
  before,
  after,
  width,
  height,
  title,
  id,
}: {
  project?: BeforeAfterProject | SliderSource;
  /** Optional overrides when not passing a full project record. */
  before?: ProjectImage;
  after?: ProjectImage;
  width?: number;
  height?: number;
  title?: string;
  id?: string;
  className?: string;
}) {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(50);
  const draggingRef = useRef(false);
  const interactionTrackedRef = useRef(false);

  const source: SliderSource = project
    ? resolveSliderSource(project)
    : {
        id: id ?? "before-after",
        title: title ?? "Before and after comparison",
        before: before ?? { src: "", alt: "" },
        after: after ?? { src: "", alt: "" },
        width: width ?? 1280,
        height: height ?? 854,
      };

  const trackInteraction = useCallback(
    (method: "pointer" | "keyboard_or_input") => {
      if (interactionTrackedRef.current) return;
      interactionTrackedRef.current = true;
      trackAnalyticsEvent({
        event: "before_after_interaction",
        project_id: source.id,
        interaction_method: method,
      });
    },
    [source.id],
  );

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(100, Math.max(0, pct)));
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current) return;
      e.preventDefault();
      updateFromClientX(e.clientX);
    };
    const onUp = () => {
      draggingRef.current = false;
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [updateFromClientX]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative isolate w-full touch-none overflow-hidden rounded-lg border border-border bg-muted select-none",
        className,
      )}
      style={{ aspectRatio: `${source.width} / ${source.height}` }}
      onPointerDown={(e) => {
        trackInteraction("pointer");
        draggingRef.current = true;
        updateFromClientX(e.clientX);
      }}
    >
      <img
        src={source.after.src}
        alt={source.after.alt}
        width={source.width}
        height={source.height}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 size-full object-cover"
        draggable={false}
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <img
          src={source.before.src}
          alt={source.before.alt}
          width={source.width}
          height={source.height}
          loading="lazy"
          decoding="async"
          className="size-full object-cover"
          draggable={false}
        />
      </div>

      <span className="pointer-events-none absolute top-3 left-3 rounded bg-slate-deep/75 px-2 py-1 text-[0.65rem] tracking-[0.12em] uppercase text-primary-foreground">
        {t.beforeAfter.beforeLabel}
      </span>
      <span className="pointer-events-none absolute top-3 right-3 rounded bg-slate-deep/75 px-2 py-1 text-[0.65rem] tracking-[0.12em] uppercase text-primary-foreground">
        {t.beforeAfter.afterLabel}
      </span>

      <div
        className="pointer-events-none absolute inset-y-0 w-px bg-background/90"
        style={{ left: `${position}%` }}
        aria-hidden="true"
      />

      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={Math.round(position)}
        onChange={(e) => {
          trackInteraction("keyboard_or_input");
          setPosition(Number(e.target.value));
        }}
        aria-label={`${t.beforeAfter.sliderLabel}: ${source.title}`}
        aria-valuetext={`${Math.round(position)}% ${t.beforeAfter.beforeLabel} / ${100 - Math.round(position)}% ${t.beforeAfter.afterLabel}`}
        className="peer absolute inset-0 z-10 size-full cursor-ew-resize opacity-0"
      />

      <div
        className="pointer-events-none absolute top-1/2 grid size-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-border bg-background shadow-elev peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2"
        style={{ left: `${position}%` }}
        aria-hidden="true"
      >
        <MoveHorizontal className="size-4 text-foreground" />
      </div>
    </div>
  );
}
